import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { spApi } from '@/lib/api-client';
import { useLocationStore } from '@/stores/location-store';

// ── Types ──

export interface VendorInvoice {
  id: string;
  tenantId: string;
  locationId: string | null;
  payeeId: string | null;
  purchaseOrderId: string | null;
  billNumber: string | null;
  invoiceNumber: string | null;
  invoiceDate: string | null;
  dueDate: string | null;
  subtotal: string;
  taxAmount: string;
  totalAmount: string;
  creditAmount: string;
  status: string;
  pdfS3Key: string | null;
  pdfFilename: string | null;
  parsedData: unknown;
  parsingConfidence: string | null;
  vendorNameRaw: string | null;
  autoPay: boolean | null;
  updateInventory: boolean;
  inventoryUpdatedAt: string | null;
  notes: string | null;
  approvedBy: string | null;
  approvedAt: string | null;
  rejectedReason: string | null;
  createdAt: string;
  updatedAt: string;
  payee?: { name: string; company: string | null } | null;
}

export interface VendorInvoiceLine {
  id: string;
  invoiceId: string;
  lineNumber: number;
  description: string;
  rawLineText: string | null;
  vendorItemCode: string | null;
  quantity: string;
  unitOfMeasure: string | null;
  unitPrice: string;
  amount: string;
  productId: string | null;
  productName: string | null;
  productBarcode: string | null;
  productCost: number | null;
  productSellingPrice: number | null;
  productUnitName: string | null;
  unitBaseQty: number | null;
  unitCost: number | null;
  unitSellingPrice: number | null;
  priceChangePct: number | null;
  priceChangeSeverity: string | null;
  existingMarginPct: number | null;
  recommendedSellingPrice: number | null;
  recommendedMarginPct: number | null;
  matchConfidence: string | null;
  matchMethod: string | null;
  isMatched: boolean;
  adjustedQuantity: string | null;
  adjustmentReason: string | null;
  isCredit: boolean;
  priceApplied: boolean;
  priceSkipped: boolean;
  isExcluded: boolean;
  detectedUnit: string | null;
  detectedBaseQty: number | null;
  detectedUnitConfidence: string | null;
  perEachCost: number | null;
  createdAt: string;
}

export interface VendorInvoiceDetail extends VendorInvoice {
  lines: VendorInvoiceLine[];
}

// ── Queries ──

export function useVendorInvoices(params?: { status?: string; payeeId?: string; locationId?: string }) {
  const selectedLocationId = useLocationStore((s) => s.selectedLocationId);
  const scopedParams =
    params?.locationId === undefined && selectedLocationId
      ? { ...(params ?? {}), locationId: selectedLocationId }
      : params;
  return useQuery({
    queryKey: ['sp', 'vendor-invoices', scopedParams],
    queryFn: () => spApi.get<{ data: VendorInvoice[] }>('/vendor-invoices', scopedParams),
  });
}

export function useVendorInvoice(id: string | null) {
  return useQuery({
    queryKey: ['sp', 'vendor-invoice', id],
    queryFn: () => spApi.get<{ data: VendorInvoiceDetail }>(`/vendor-invoices/${id}`),
    enabled: !!id,
  });
}

export function useVendorInvoiceBills(
  params?: { payeeId?: string; locationId?: string },
  // `enabled: false` keeps the request off where the invoice processor module
  // isn't on for the location — the endpoint rejects the call there.
  options?: { enabled?: boolean },
) {
  const selectedLocationId = useLocationStore((s) => s.selectedLocationId);
  const scopedParams =
    params?.locationId === undefined && selectedLocationId
      ? { ...(params ?? {}), locationId: selectedLocationId }
      : params;
  return useQuery({
    queryKey: ['sp', 'vendor-invoice-bills', scopedParams],
    queryFn: () => spApi.get<{ data: VendorInvoice[] }>('/vendor-invoices/bills', scopedParams),
    enabled: options?.enabled ?? true,
  });
}

export interface ProductPriceLocation {
  locationId: string;
  locationName: string;
  currentCost: number | null;
  currentSellingPrice: number | null;
  history: {
    cost: number;
    sellingPrice: number;
    vendorName: string | null;
    invoiceNumber: string | null;
    invoiceDate: string | null;
    appliedAt: string;
  }[];
}

export function useProductPriceHistory(productId: string | null) {
  return useQuery({
    queryKey: ['sp', 'product-price-history', productId],
    queryFn: () =>
      spApi.get<{ data: { locations: ProductPriceLocation[] } }>(`/vendor-invoices/product-price-history/${productId}`),
    enabled: !!productId,
  });
}

export function useInvoicePdfUrl(id: string | null) {
  return useQuery({
    queryKey: ['sp', 'vendor-invoice-pdf', id],
    queryFn: () => spApi.get<{ data: { url: string } }>(`/vendor-invoices/${id}/pdf`),
    enabled: !!id,
  });
}

// ── Mutations ──

export function useUploadInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      const response = await fetch('/sp-api/v1/vendor-invoices/upload', {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({ error: { message: 'Upload failed' } }));
        throw err;
      }
      return response.json() as Promise<{ data: VendorInvoice }>;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sp', 'vendor-invoices'] }),
  });
}

export function useParseInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => spApi.post<{ data: VendorInvoiceDetail }>(`/vendor-invoices/${id}/parse`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sp', 'vendor-invoices'] });
      qc.invalidateQueries({ queryKey: ['sp', 'vendor-invoice'] });
    },
  });
}

export function useUpdateInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: { id: string } & Record<string, unknown>) =>
      spApi.put<{ data: VendorInvoice }>(`/vendor-invoices/${id}`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sp', 'vendor-invoices'] });
      qc.invalidateQueries({ queryKey: ['sp', 'vendor-invoice'] });
    },
  });
}

export function useUpdateInvoiceLine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ invoiceId, lineId, ...data }: { invoiceId: string; lineId: string } & Record<string, unknown>) =>
      spApi.put<{ data: VendorInvoiceLine }>(`/vendor-invoices/${invoiceId}/lines/${lineId}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sp', 'vendor-invoice'] }),
  });
}

export function useAddInvoiceLine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ invoiceId, ...data }: { invoiceId: string } & Record<string, unknown>) =>
      spApi.post<{ data: VendorInvoiceLine }>(`/vendor-invoices/${invoiceId}/lines`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sp', 'vendor-invoice'] }),
  });
}

export function useDeleteInvoiceLine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ invoiceId, lineId }: { invoiceId: string; lineId: string }) =>
      spApi.delete(`/vendor-invoices/${invoiceId}/lines/${lineId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sp', 'vendor-invoice'] }),
  });
}

export function useRestoreInvoiceLine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ invoiceId, lineId }: { invoiceId: string; lineId: string }) =>
      spApi.post(`/vendor-invoices/${invoiceId}/lines/${lineId}/restore`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sp', 'vendor-invoice'] }),
  });
}

export function useConfirmUnit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ invoiceId, lineId }: { invoiceId: string; lineId: string }) =>
      spApi.post<{
        data: {
          lineId: string;
          productId: string;
          unitName: string;
          baseQty: number;
          perEachCost: number;
          confidence: string;
          productUnitCreated: boolean;
        };
      }>(`/vendor-invoices/${invoiceId}/lines/${lineId}/confirm-unit`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sp', 'vendor-invoice'] }),
  });
}

export function useApplyLinePrice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ invoiceId, lineId, marginPct }: { invoiceId: string; lineId: string; marginPct?: number }) =>
      spApi.post<{
        data: {
          lineId: string;
          productId: string;
          newCost: number;
          newSellingPrice: number;
          marginPct: number;
          priceApplied: boolean;
        };
      }>(`/vendor-invoices/${invoiceId}/lines/${lineId}/apply-price`, marginPct != null ? { marginPct } : undefined),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sp', 'vendor-invoice'] }),
  });
}

export function useRevertInventory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => spApi.post(`/vendor-invoices/${id}/revert-inventory`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sp', 'vendor-invoice'] }),
  });
}

export interface ApprovalPreviewItem {
  lineNumber: number;
  productId: string;
  productName: string;
  description: string;
  invoiceQuantity: number;
  invoiceUnit: string;
  quantity: number;
  unitOfMeasure: string;
  converted: boolean;
  currentStock: number;
  afterStock: number;
  priceApplied: boolean;
  unitPrice: number;
  amount: number;
}

// ── Attachments ──

export interface InvoiceAttachment {
  id: string;
  invoice_id: string;
  filename: string;
  s3_key: string;
  content_type: string;
  file_size: number;
  created_at: string;
}

export function useInvoiceAttachments(invoiceId: string | null) {
  return useQuery({
    queryKey: ['sp', 'invoice-attachments', invoiceId],
    queryFn: () => spApi.get<{ data: InvoiceAttachment[] }>(`/vendor-invoices/${invoiceId}/attachments`),
    enabled: !!invoiceId,
  });
}

export function useUploadAttachment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ invoiceId, file }: { invoiceId: string; file: File }) => {
      const formData = new FormData();
      formData.append('file', file);
      const response = await fetch(`/sp-api/v1/vendor-invoices/${invoiceId}/attachments`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({ error: { message: 'Upload failed' } }));
        throw err;
      }
      return response.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sp', 'invoice-attachments'] }),
  });
}

export function useDeleteAttachment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ invoiceId, attachmentId }: { invoiceId: string; attachmentId: string }) =>
      spApi.delete(`/vendor-invoices/${invoiceId}/attachments/${attachmentId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sp', 'invoice-attachments'] }),
  });
}

export function useApprovalPreview(id: string | null, enabled: boolean) {
  return useQuery({
    queryKey: ['sp', 'approval-preview', id],
    queryFn: () =>
      spApi.get<{
        data: { invoiceId: string; invoiceNumber: string; totalItems: number; items: ApprovalPreviewItem[] };
      }>(`/vendor-invoices/${id}/approval-preview`),
    enabled: !!id && enabled,
  });
}

export function useUpdateInventory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => spApi.post(`/vendor-invoices/${id}/update-inventory`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sp', 'vendor-invoice'] }),
  });
}

export function useMatchProducts() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      spApi.post<{ data: { lines: VendorInvoiceLine[] } }>(`/vendor-invoices/${id}/match-products`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sp', 'vendor-invoice'] }),
  });
}

export function useApproveInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, updateInventory }: { id: string; updateInventory: boolean }) =>
      spApi.post<{ data: VendorInvoice }>(`/vendor-invoices/${id}/approve`, { updateInventory }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sp', 'vendor-invoices'] });
      qc.invalidateQueries({ queryKey: ['sp', 'vendor-invoice'] });
      qc.invalidateQueries({ queryKey: ['sp', 'vendor-invoice-bills'] });
    },
  });
}

export function useRejectInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      spApi.post<{ data: VendorInvoice }>(`/vendor-invoices/${id}/reject`, { reason }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sp', 'vendor-invoices'] });
      qc.invalidateQueries({ queryKey: ['sp', 'vendor-invoice'] });
    },
  });
}

export function usePayBill() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: { id: string; fundingAccountId: string; paymentMethod?: string; memo?: string }) =>
      spApi.post<{ data: unknown }>(`/vendor-invoices/${id}/pay`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sp', 'vendor-invoices'] });
      qc.invalidateQueries({ queryKey: ['sp', 'vendor-invoice'] });
      qc.invalidateQueries({ queryKey: ['sp', 'vendor-invoice-bills'] });
      qc.invalidateQueries({ queryKey: ['sp', 'payments'] });
      qc.invalidateQueries({ queryKey: ['sp', 'dashboard'] });
    },
  });
}

export interface POSuggestion {
  id: string;
  orderNumber: string;
  matchedItems: number;
  totalItems: number;
}

export function useSuggestPO(invoiceId: string | null) {
  return useQuery({
    queryKey: ['sp', 'vendor-invoice-suggest-po', invoiceId],
    queryFn: () =>
      spApi.get<{ data: { suggested: POSuggestion | null; available: POSuggestion[] } }>(
        `/vendor-invoices/${invoiceId}/suggest-po`,
      ),
    enabled: !!invoiceId,
  });
}

export function useLinkPO() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, purchaseOrderId }: { id: string; purchaseOrderId: string }) =>
      spApi.post<{ data: VendorInvoice }>(`/vendor-invoices/${id}/link-po`, { purchaseOrderId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sp', 'vendor-invoice'] }),
  });
}
